import { type CountryCode, type ProjectCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { AppliedStatus, type SelectedCouponGiveaway, type SelectedCouponGiveawayProduct } from '@hooks-api/types/dataModel/coupon'
import { type DeliveryClass } from '@hooks-api/types/dataModel/shipping'
import { useCallback, useMemo } from 'react'
import * as couponService from '../../core/couponService'
import { parseGiveaways } from '../../parser/parseGiveaways'
import { type MergedCoupon, type SelectedCouponGiveawayProductMap } from '../../types'
import { cartStoreMap, type PlatformType } from './store-map'
import { useFpB2bCoupons } from './use-fp-b2b-coupons'

export const useFpB2bCouponService = (campaignId: number, projectCode: ProjectCode, countryCode: CountryCode, campaignCode: string | undefined, platform: PlatformType) => {
  const { useCartStore } = cartStoreMap[platform]
  const [cartValues, setCartValues] = useCartStore(campaignId)
  const { availableMergedCoupons, calculateResult } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)
  // NOTE: 已達標但已贈完後端欄位會是 unable，也要顯示出來
  const appliedGiveaways = useMemo(() => parseGiveaways(availableMergedCoupons)?.filter(giveaway => giveaway.applied_status === AppliedStatus.APPLIED || giveaway.applied_status === AppliedStatus.UNABLE), [availableMergedCoupons])

  const addManualInputCouponIds = useCallbackEvent((couponId: number[]) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.addManualInputCouponIds(state.coupon, couponId),
    }))
  })

  const removeManualInputCouponIds = useCallbackEvent((couponId: number[]) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.removeManualInputCouponIds(state.coupon, couponId),
    }))
  })

  const updateSelectedGiveaways = useCallbackEvent((
    couponCriterionId: SelectedCouponGiveaway['coupon_criterion_id'],
    couponGiveawayProductMap: SelectedCouponGiveawayProductMap,
  ) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.updateSelectedGiveaways(state.coupon, couponCriterionId, couponGiveawayProductMap),
    }))
  })

  const updateSelectedRegularGiveaways = useCallbackEvent((
    couponCriterionId: SelectedCouponGiveaway['coupon_criterion_id'],
    product: Omit<SelectedCouponGiveawayProduct, 'quantity'>,
  ) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.updateSelectedRegularGiveaways(state.coupon, couponCriterionId, product),
    }))
  })

  const removeSelectedRegularGiveaways = useCallbackEvent((
    couponCriterionIds: SelectedCouponGiveaway['coupon_criterion_id'] | SelectedCouponGiveaway['coupon_criterion_id'][],
  ) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.removeSelectedRegularGiveaways(state.coupon, couponCriterionIds),
    }))
  })

  const removeSelectedGiveaways = useCallbackEvent((
    couponCriterionIds: SelectedCouponGiveaway['coupon_criterion_id'] | SelectedCouponGiveaway['coupon_criterion_id'][],
  ) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.removeSelectedGiveaways(state.coupon, couponCriterionIds),
    }))
  })

  const clearInvalidSelectedGiveaways = useCallback(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.clearInvalidSelectedGiveaways(state.coupon, calculateResult?.applied_coupons),
    }))
  }, [calculateResult?.applied_coupons, setCartValues])

  const clearInvalidManualInputCouponIds = useCallback(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.clearInvalidManualInputCouponIds(state.coupon, calculateResult?.cart_validation_messages),
    }))
  }, [calculateResult?.cart_validation_messages, setCartValues])

  const resetSelectedGiveaways = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.resetSelectedGiveaways(state.coupon),
    }))
  })

  const resetSelectedRegularGiveaways = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.resetSelectedRegularGiveaways(state.coupon),
    }))
  })

  const resetManualInputCouponIds = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.resetManualInputCouponIds(state.coupon),
    }))
  })

  const setSelectedManualCouponId = useCallbackEvent((couponId: number) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.setSelectedManualCouponId(state.coupon, couponId),
    }))
  })

  const resetCouponsAndGiveaways = useCallbackEvent(() => {
    resetManualInputCouponIds()
    resetSelectedGiveaways()
    resetSelectedRegularGiveaways()
  })

  const selectedCouponGiveawayMap = useMemo(() =>
    couponService.getSelectedCouponGiveawayMap([...cartValues.coupon.selectedGiveaways, ...cartValues.coupon.selectedRegularGiveaways]),
  [cartValues.coupon.selectedGiveaways, cartValues.coupon.selectedRegularGiveaways])

  const setDefaultSelectedGiveaways = useCallback(() => {
    if (appliedGiveaways) {
      setCartValues(state => ({
        ...state,
        coupon: couponService.setDefaultSelectedGiveaways(state.coupon, appliedGiveaways),
      }))
    }
  }, [appliedGiveaways, setCartValues])

  const regularGiveaways = useMemo(() => availableMergedCoupons?.filter(coupon => coupon.is_regular_giveaway) || [], [availableMergedCoupons])

  const getRegularGiveawayProgress = useCallback((regularGiveaways: MergedCoupon[]) => couponService.getRegularGiveawayProgress(regularGiveaways, calculateResult?.total_after_discount), [calculateResult?.total_after_discount])

  const getRegularGiveawaysFilteredByDeliveryClass = useCallback((deliveryClass: DeliveryClass) =>
    regularGiveaways?.filter(regularGiveaway => regularGiveaway.productDeliveryClasses.includes(deliveryClass)),
  [regularGiveaways])

  return {
    ...cartValues.coupon,
    appliedGiveaways,
    regularGiveaways,
    getRegularGiveawayProgress,
    addManualInputCouponIds,
    removeManualInputCouponIds,
    updateSelectedGiveaways,
    updateSelectedRegularGiveaways,
    setDefaultSelectedGiveaways,
    removeSelectedRegularGiveaways,
    removeSelectedGiveaways,
    selectedCouponGiveawayMap,
    getRegularGiveawaysFilteredByDeliveryClass,
    clearInvalidSelectedGiveaways,
    clearInvalidManualInputCouponIds,
    setSelectedManualCouponId,
    resetCouponsAndGiveaways,
  }
}
